import {
  AssignMemberSelect,
  ChangeStatusSelect,
  DocumentDeleteSelected,
  InvoiceDeleteSelected,
  LinkDeleteSelected,
  LinkSourceTypeSelect,
  LinkStatusSelect,
} from '@molecules';
import { CopyrightStatusSelect } from '@molecules/Selects/CopyrightStatusSelect';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkManagementPrioritySelect from '@organisms/Enterprise/LinksManagement/LinkManagementPrioritySelect/LinkManagementPrioritySelect';
import LinkManagementSaveBtn from '@organisms/Enterprise/LinksManagement/LinkManagementSaveBtn/LinkManagementSaveBtn';
import LinkManagementSourceTypeSelect from '@organisms/Enterprise/LinksManagement/LinkManagementSourceTypeSelect/LinkManagementSourceTypeSelect';
import { LinkReviewStatusSelect } from '@molecules/Selects/LinkReviewStatusSelect';
import {
  LinkReviewCategory,
  LinkManagementStatus,
} from '@organisms/Tables/Enterprise/LinksReviewTable/types';
import { LinkReviewCategorySelect } from '@molecules/Selects/LinkReviewCategorySelect';
import LinkManagementStatusSelect from '@organisms/Enterprise/LinksManagement/LinkManagementStatusSelect/LinkManagementStatusSelect';
import { SelectLinkStatuses, SourceType, TableSelectType, warningMessage } from '@services';
import { useState } from 'react';
import styled from 'styled-components';
import { customResponsiveStylesForSelect } from './SelectedRow.styles';
import LinkManagementDeleteBtn from '@organisms/Enterprise/LinksManagement/LinkManagementDeleteBtn/LinkManagementDeleteBtn';
import { SelectCopyrightStatuses } from '@services/types/copyright';

const Wrap = styled(Box)`
  display: flex;
  align-items: center;
  background: #daeffe;
  border: 1px solid #b4dffd;
  border-radius: 8px;
  height: 70px;
  justify-content: space-between;
  padding: 0 16px;
  margin-bottom: 16px;
`;

interface SelectedRowProps<T> {
  items: T[];
  count: number;
  cancel: () => void;
  refetch: () => void;
  value?: string;
  multipleValue?: any;
  type?: TableSelectType;
}

export const SelectedRow = <T extends string>({
  items,
  count,
  cancel,
  value,
  multipleValue,
  refetch,
  type = TableSelectType.Link,
}: SelectedRowProps<T>) => {
  const [savedDataSelectedRow, setSavedDataSelectedRow] = useState<any | null>(null);

  const safeJsonParse = (str: string) => {
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  const parseData = (data: T[]) => data.map(safeJsonParse).flat();

  const onClickOpen = () => {
    for (const url of multipleValue.urls) {
      const win = window.open(url, '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        warningMessage(
          'Popup was blocked! Please allow popups for this site to open all links in Pop-ups and redirects settings.',
        );
        break;
      }
    }
  };

  const renderSelect = () => {
    switch (type) {
      case TableSelectType.Account:
        return <AssignMemberSelect value={value} memberIds={items} refetch={refetch} />;
      case TableSelectType.Assign:
        return null;
      case TableSelectType.ChangeStatus:
        return <ChangeStatusSelect value={value} linkList={items} refetch={refetch} />;
      case TableSelectType.Document:
        return (
          <Box display="flex" alignItems="center">
            <DocumentDeleteSelected documents={items} refetch={refetch} />
          </Box>
        );
      case TableSelectType.Invoices:
        return (
          <Box display="flex" alignItems="center">
            <InvoiceDeleteSelected invoices={items} refetch={refetch} />
          </Box>
        );
      case TableSelectType.Copyright:
        return (
          <Box display="flex" alignItems="center">
            <CopyrightStatusSelect
              value={value as SelectCopyrightStatuses}
              links={items}
              refetch={refetch}
            />
          </Box>
        );
      case TableSelectType.SocialMediaLink:
        return null;
      case TableSelectType.LinkManagement:
        return (
          <Box display="flex" alignItems="center">
            <LinkManagementSourceTypeSelect
              savedDataSelectedRow={savedDataSelectedRow}
              setSavedDataSelectedRow={setSavedDataSelectedRow}
              customSxForSelect={{
                ...customResponsiveStylesForSelect,
              }}
              disabled
            />
            <LinkManagementStatusSelect
              savedDataSelectedRow={savedDataSelectedRow}
              setSavedDataSelectedRow={setSavedDataSelectedRow}
              customSxForSelect={{
                ...customResponsiveStylesForSelect,
              }}
            />
            <LinkManagementPrioritySelect
              savedDataSelectedRow={savedDataSelectedRow}
              setSavedDataSelectedRow={setSavedDataSelectedRow}
              customSxForSelect={{
                ...customResponsiveStylesForSelect,
              }}
              disabled
            />
            <LinkManagementSaveBtn
              multipleValue={multipleValue}
              savedDataSelectedRow={savedDataSelectedRow}
              refetch={refetch}
            />
            <LinkManagementDeleteBtn multipleValue={multipleValue} refetch={refetch} />
          </Box>
        );
      case TableSelectType.LinksReview:
        return (
          <Box display="flex" alignItems="center" gap="12px">
            <LinkReviewStatusSelect
              value={multipleValue?.status as LinkManagementStatus}
              links={parseData(items)}
            />
            <LinkReviewCategorySelect
              value={multipleValue?.category as LinkReviewCategory}
              links={parseData(items)}
            />
            <Tooltip title="Open selected domains in new tab">
              <OpenInNewIcon
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#44AEFB',
                  },
                }}
                onClick={onClickOpen}
              />
            </Tooltip>
          </Box>
        );
      default:
        return (
          <Box display="flex" alignItems="center">
            <LinkSourceTypeSelect
              value={multipleValue?.sourceType as SourceType | null | 'mixed'}
              links={parseData(items)}
              refetch={refetch}
            />
            <LinkStatusSelect
              value={multipleValue?.status as SelectLinkStatuses}
              links={parseData(items)}
              refetch={refetch}
            />
            <LinkDeleteSelected links={parseData(items)} refetch={refetch} />
          </Box>
        );
    }
  };

  return (
    <Wrap>
      <Box display="flex" alignItems="center">
        <IconButton sx={{ mr: '16px' }} onClick={cancel}>
          <CloseIcon />
        </IconButton>
        {count} items selected
      </Box>
      {renderSelect()}
    </Wrap>
  );
};
